// ============================================================================
// TanStack Start-specifik fil — se README.md → "Arkitektur-noter" → "src/start.ts"
// ----------------------------------------------------------------------------
// Denne fil bruger `createStart` + `createMiddleware` fra @tanstack/react-start
// og er KUN gyldig under TanStack Start. Ved migration til et andet framework
// (Next.js, Remix, ren Vite-SPA, ...) skal filen slettes og header-injection
// genimplementeres med det nye frameworks tilsvarende mekanisme.
//
// Adfærdsspecifikation til genimplementering:
//   src/lib/__tests__/start-middleware.test.ts
// ============================================================================
import { createStart, createMiddleware } from "@tanstack/react-start";
import { setResponseHeader } from "@tanstack/react-start/server";
import { supabase } from "@/integrations/supabase/client";
import { getRequestId } from "@/lib/request-id";
import { htmlErrorPage, isHtmlPreferred } from "@/lib/server-handler";
import { reportError } from "@/lib/error-reporter";
import { logDealerGeoStartupWarning } from "@/lib/dealer-geo";
import { createServerOnlyBoot } from "@/lib/server-only-boot";
import { attachSupabaseAuth } from "@/integrations/supabase/auth-attacher";

// Run config validations once at module load (server boot). Currently:
//  - dealership lat/lng presence + bounds — required for ad-feed geo.
// Side-effect-only call; the helper is idempotent so hot-reload won't spam.
logDealerGeoStartupWarning();

// Seed the in-memory dealer-geo override cache from `site_settings` so SSR
// JSON-LD and ad feeds reflect any admin-edited coordinates from the very
// first request. `createServerOnlyBoot` guarantees:
//   - the `.server.ts` module is never pulled into the client bundle
//     (client impl is a no-op, dynamic import gated by import-protection),
//   - the initializer runs at most once per server process,
//   - load/run failures are logged but never crash SSR.
const bootDealerGeoOverrideOnce = createServerOnlyBoot({
  name: "dealer-geo-override",
  load: () => import("@/lib/dealer-geo.server"),
  run: async (mod) => {
    await mod.loadDealerGeoOverrideFromDb();
  },
});

/**
 * Global REQUEST middleware der tildeler/propagerer en correlation-id
 * (`x-request-id`) for ALLE indkommende requests (SSR, server routes,
 * server functions). Logger en struktureret start- og slut-linje pr.
 * request, så vi kan finde den nøjagtige route der trigger en HTTPError
 * eller syntax-failure.
 *
 * Catch-all error handler:
 *   Hvis en handler/SSR render kaster en uventet exception (dvs. ikke
 *   håndteret af `withErrorBoundary`), konverterer vi den til en pæn
 *   branded HTML/JSON-fejlside i stedet for frameworkets råe
 *   `{"status":500,"unhandled":true,"message":"HTTPError"}`-payload.
 */
const requestLogger = createMiddleware().server(async ({ next, request }) => {
  const id = getRequestId(request);
  const url = new URL(request.url);
  const path = url.pathname + url.search;
  const startedAt = Date.now();

  try {
    setResponseHeader("x-request-id", id);
  } catch {
    // setResponseHeader can throw outside a request context — safe to ignore.
  }

  bootDealerGeoOverrideOnce();

  console.log(`[req] req_id=${id} method=${request.method} url=${path} phase=start`);

  try {
    const result = await next();
    const dur = Date.now() - startedAt;
    console.log(
      `[req] req_id=${id} method=${request.method} url=${path} phase=end dur_ms=${dur}`,
    );
    return result;
  } catch (err) {
    const dur = Date.now() - startedAt;
    const rawMessage = err instanceof Error ? err.message : String(err);
    console.error(
      `[req] req_id=${id} method=${request.method} url=${path} phase=error dur_ms=${dur} error="${rawMessage.replace(/"/g, "'").slice(0, 300)}"`,
    );
    if (err instanceof Error && err.stack) {
      console.error(`[req] req_id=${id} stack:\n${err.stack}`);
    }

    // Webhook-alert til Discord/Slack — samme logik som withErrorBoundary
    // for konsistens når SSR/framework crasher uden for en route handler.
    void reportError({
      source: "server-route",
      status: 500,
      method: request.method,
      url: path,
      userAgent: request.headers.get("user-agent") ?? undefined,
      message: rawMessage || "Unhandled SSR error",
      stack: err instanceof Error ? err.stack : undefined,
      extra: { req_id: id },
    });

    // Returnér en pæn fejlside i stedet for at lade frameworket emit'e
    // `{"status":500,"unhandled":true,"message":"HTTPError"}`-JSON.
    const friendly = "Der opstod en uventet fejl. Prøv igen om lidt.";
    const headers: Record<string, string> = { "x-request-id": id };
    if (isHtmlPreferred(request)) {
      return new Response(htmlErrorPage(500, friendly, id), {
        status: 500,
        headers: { ...headers, "Content-Type": "text/html; charset=utf-8" },
      });
    }
    return new Response(
      JSON.stringify({ ok: false, status: 500, error: friendly, req_id: id }),
      { status: 500, headers: { ...headers, "Content-Type": "application/json" } },
    );
  }
});

/**
 * Global server-function-middleware der automatisk tilføjer
 * `Authorization: Bearer <access_token>` til ALLE klient-kald af
 * createServerFn'er. Dermed virker `requireSupabaseAuth`-middlewaren
 * uden at hver enkelt UI-komponent skal sende headeren manuelt.
 *
 * - Kører kun på klient (.client()): på serveren har vi allerede
 *   request-headers via getRequest() i requireSupabaseAuth.
 * - SSR-safe: localStorage findes ikke under SSR, så vi guarder med
 *   `typeof window`. Server-funktioner der kræver auth, kaldes alligevel
 *   først efter hydration fra UI-komponenter.
 * - Hvis der ikke er en session, sender vi ingen header og lader
 *   middlewaren returnere 401 som normalt — det er korrekt opførsel
 *   for ikke-loggede brugere.
 */
const injectSupabaseAuthHeader = createMiddleware({ type: "function" }).client(
  async ({ next }) => {
    if (typeof window === "undefined") {
      return next();
    }
    try {
      const { data } = await supabase.auth.getSession();
      const token = data.session?.access_token;
      if (!token) {
        return next();
      }
      return next({
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch {
      // Fail-open: hvis getSession crasher, lad request gå igennem og
      // lad serveren afvise med 401 hvis det er en beskyttet route.
      return next();
    }
  },
);

export const startInstance = createStart(() => ({
  requestMiddleware: [requestLogger],
  functionMiddleware: [attachSupabaseAuth, injectSupabaseAuthHeader],
}));
